import { arrayMove } from '@dnd-kit/sortable';
import { hasFirstRule, hasLastRule, type Chain } from './layout';
import { disconnectItem, insertBetween, reorderChain } from './mutations';
import { hasAnchorViolation, hasCycle } from './validation';
import type { EditorNode } from './types';

// Droppable id of the Flex pool container.
export const FREE_POOL_ID = 'free-pool';

const GAP_ID_PREFIX = 'gap:';

// Droppable id of the gap marker between two chain positions.
export const gapDropId = (chainId: string, position: number): string =>
  `${GAP_ID_PREFIX}${chainId}:${position}`;

export const isGapDropId = (overId: string): boolean => overId.startsWith(GAP_ID_PREFIX);

// Whether a block may be dropped on the gap at `position` in the chain
// (0 = before the head, items.length = after the tail). Nothing may sit after
// a LAST-anchored block or before a FIRST-anchored block, so those gaps are
// off-limits.
export const isGapDropAllowed = (chain: Chain, position: number): boolean => {
  const prev = position > 0 ? chain.items[position - 1] : undefined;
  const next = chain.items[position];
  if (prev && hasLastRule(prev)) return false;
  if (next && hasFirstRule(next)) return false;
  return true;
};

// Drop on a gap marker between two chain positions ("gap:<chainId>:<pos>").
// Returns null when the gap's chain no longer exists or the gap is off-limits.
export const resolveGapDrop = (
  editor: EditorNode[],
  chains: Chain[],
  activeWiId: string,
  overId: string
): EditorNode[] | null => {
  const parts = overId.split(':');
  const chainId = parts[1];
  const pos = Number.parseInt(parts[2], 10);
  const chain = chains.find((c) => c.id === chainId);
  if (!chain) return null;
  if (!isGapDropAllowed(chain, pos)) return null;
  const prevId = pos > 0 ? chain.items[pos - 1]?.id : undefined;
  const nextId = chain.items[pos]?.id;
  return insertBetween(editor, activeWiId, prevId, nextId);
};

const reorderWithinChain = (
  editor: EditorNode[],
  chain: Chain,
  activeWiId: string,
  overId: string
): EditorNode[] => {
  const oldIdx = chain.items.findIndex((wi) => wi.id === activeWiId);
  const newIdx = chain.items.findIndex((wi) => wi.id === overId);
  if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx) return editor;
  return reorderChain(editor, chain.items, arrayMove(chain.items, oldIdx, newIdx));
};

const moveIntoChain = (
  editor: EditorNode[],
  targetChain: EditorNode[],
  activeWiId: string,
  overId: string,
  isActiveInChain: boolean
): EditorNode[] => {
  const base = isActiveInChain ? disconnectItem(editor, activeWiId) : editor;
  const overIdx = targetChain.findIndex((wi) => wi.id === overId);
  return insertBetween(base, activeWiId, targetChain[overIdx]?.id, targetChain[overIdx + 1]?.id);
};

const reorderFreePool = (
  editor: EditorNode[],
  free: EditorNode[],
  activeWiId: string,
  overId: string
): EditorNode[] => {
  const oldIdx = free.findIndex((wi) => wi.id === activeWiId);
  const newIdx = free.findIndex((wi) => wi.id === overId);
  if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx) return editor;
  const reorderedFree = arrayMove(free, oldIdx, newIdx);
  const freeIdSet = new Set(free.map((wi) => wi.id));
  return [...editor.filter((wi) => !freeIdSet.has(wi.id)), ...reorderedFree];
};

// Drop on a node or the free pool: reorder within a chain, detach to the free
// pool, move into another chain, or reorder the free pool. Falls through to the
// unchanged editor when the drop matches none of these.
export const resolveDrop = (
  editor: EditorNode[],
  chains: Chain[],
  free: EditorNode[],
  activeWiId: string,
  overId: string
): EditorNode[] => {
  const activeChainIdx = chains.findIndex((c) => c.items.some((wi) => wi.id === activeWiId));
  const activeIsInFree = free.some((wi) => wi.id === activeWiId);
  const overChainIdx = chains.findIndex(
    (c) => c.items.some((wi) => wi.id === overId) || c.id === overId
  );
  const overIsInFree = free.some((wi) => wi.id === overId) || overId === FREE_POOL_ID;

  if (activeChainIdx !== -1 && overChainIdx === activeChainIdx) {
    return reorderWithinChain(editor, chains[activeChainIdx], activeWiId, overId);
  }
  if (activeChainIdx !== -1 && overIsInFree) {
    return disconnectItem(editor, activeWiId);
  }
  if (
    (activeChainIdx !== -1 || activeIsInFree) &&
    overChainIdx !== -1 &&
    overChainIdx !== activeChainIdx
  ) {
    return moveIntoChain(editor, chains[overChainIdx].items, activeWiId, overId, activeChainIdx !== -1);
  }
  if (activeIsInFree && overIsInFree) {
    return reorderFreePool(editor, free, activeWiId, overId);
  }
  return editor;
};

// Single entry point for a finished drag: gap drops insert between two chain
// positions, everything else goes through node/pool resolution. Returns null
// when the drop must be ignored (the card snaps back).
export const resolveDragEnd = (
  editor: EditorNode[],
  chains: Chain[],
  free: EditorNode[],
  activeWiId: string,
  overId: string
): EditorNode[] | null =>
  isGapDropId(overId)
    ? resolveGapDrop(editor, chains, activeWiId, overId)
    : resolveDrop(editor, chains, free, activeWiId, overId);

// resolveDragEnd plus the final validity guards: null when the drop would
// introduce a contradictory loop or place a block after a LAST / before a
// FIRST anchor. Shared by the drag-end commit and by collision detection, so
// a spot that can't be committed is never offered — or previewed — as a drop
// target while dragging.
export const resolveValidDragEnd = (
  editor: EditorNode[],
  chains: Chain[],
  free: EditorNode[],
  activeWiId: string,
  overId: string
): EditorNode[] | null => {
  const next = resolveDragEnd(editor, chains, free, activeWiId, overId);
  return next !== null && !hasCycle(next) && !hasAnchorViolation(next) ? next : null;
};
